import React, { useMemo, useState } from "react";
import {
  Sparkles,
  SparklesIcon,
  Monitor,
  Wifi,
  FlaskConical,
  Presentation,
  Laptop,
  Tv,
  BookOpen,
  Headphones,
  Eye,
  Hand,
  Search,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useAuth } from "../../context/AuthContext";
import { updateChallengeProgress } from "../../lib/supabase"; 
import toast from "react-hot-toast";


const createDynamicLearningGoals = (topic: string): string[] => {
  const fallbackTopic = "materi pembelajaran yang dipelajari";
  const normalizedTopic = topic.trim() || fallbackTopic;
  const keyPhrases = normalizedTopic
    .split(/[,;]| dan | serta |\/|\n/)
    .map(phrase => phrase.trim())
    .filter(Boolean);

  const topics = keyPhrases.length > 0 ? keyPhrases : [normalizedTopic];
  const primary = topics[0];
  const secondary = topics[1] || primary;
  const tertiary = topics[2] || secondary;

  const uniqueGoals = new Set<string>();
  const addGoals = (goals: string[]) => {
    goals.forEach(goal => {
      if (goal.trim()) {
        uniqueGoals.add(goal);
      }
    });
  };

  addGoals([
    `Siswa mampu menjelaskan konsep utama mengenai ${primary}`,
    `Siswa dapat menganalisis relevansi ${primary} dalam konteks kehidupan sehari-hari`,
    `Siswa mampu mengidentifikasi istilah penting yang berkaitan dengan ${primary}`,
    `Siswa dapat menyusun rangkuman informatif tentang ${primary}`,
    `Siswa mampu menyajikan hasil kajian mengenai ${primary} secara kreatif`,
  ]);

  topics.slice(0, 3).forEach(phrase => {
    addGoals([
      `Siswa dapat mengembangkan pertanyaan inkuiri mendalam tentang ${phrase}`,
      `Siswa mampu mengaitkan ${phrase} dengan pengalaman nyata di lingkungan peserta didik`,
      `Siswa dapat merancang aktivitas belajar untuk menguatkan pemahaman terkait ${phrase}`,
    ]);
  });

  addGoals([
    `Siswa mampu mengevaluasi dampak ${tertiary} terhadap perkembangan lingkungan sekitar`,
    `Siswa dapat memanfaatkan teknologi untuk mengeksplorasi informasi mengenai ${secondary}`,
    `Siswa mampu bekerja kolaboratif dalam memecahkan masalah yang berkaitan dengan ${secondary}`,
    `Siswa dapat mengomunikasikan hasil pembelajaran ${primary} menggunakan media yang variatif`,
    `Siswa mampu melakukan refleksi kritis terhadap proses pembelajaran ${primary}`,
    `Siswa dapat menilai keakuratan sumber informasi terkait ${primary} secara objektif`,
  ]);

  if (primary !== secondary) {
    addGoals([
      `Siswa mampu membandingkan ${primary} dengan ${secondary} untuk menemukan persamaan dan perbedaan penting`,
      `Siswa dapat menjelaskan hubungan sebab-akibat antara ${primary} dan ${secondary}`,
    ]);
  }

  if (secondary !== tertiary) {
    addGoals([
      `Siswa mampu mengidentifikasi faktor pendukung dan penghambat ${secondary}`,
      `Siswa dapat merumuskan rekomendasi tindak lanjut terkait ${tertiary} berdasarkan hasil analisis`,
    ]);
  }

  if (uniqueGoals.size < 10) {
    addGoals([
      `Siswa mampu mengembangkan solusi kreatif terhadap tantangan yang muncul pada kajian ${primary}`,
      `Siswa dapat menghubungkan ${primary} dengan profil Pelajar Pancasila`,
      `Siswa mampu menerapkan nilai karakter positif saat mempelajari ${primary}`,
    ]);
  }

  return Array.from(uniqueGoals).slice(0, 15);
};

type SaranaPrasarana = {
  proyektor: boolean;
  internet: boolean;
  laboratorium: boolean;
  papanTulisDigital: boolean;
  komputer: boolean;
  lcdTv: boolean;
  perpustakaan: boolean;
};

interface LessonPlanFormProps {
  formData: {
    mataPelajaran: string;
    jenjangPendidikan: string;
    kelas: string;
    kurikulum: string;
    topik: string;
    tujuanPembelajaran: string;
    kriteriaTambahan: string;
  };
  handleInputChange: (field: string, value: string) => void;
  isFormValid: boolean;
  isGenerating: boolean;
  progress: number;
  handleGenerateNow: () => void;
  handleEnhancePrompt: () => void;
  saranaPrasarana: SaranaPrasarana;
  setSaranaPrasarana: React.Dispatch<React.SetStateAction<SaranaPrasarana>>;
  keteranganSarpras: string;
  setKeteranganSarpras: (value: string) => void;
  gayaBelajar: { audio: boolean; visual: boolean; kinestetik: boolean };
  setGayaBelajar: React.Dispatch<
    React.SetStateAction<{
      audio: boolean;
      visual: boolean;
      kinestetik: boolean;
    }>
  >;
  selectedLearningGoals: string[];
  setSelectedLearningGoals: React.Dispatch<React.SetStateAction<string[]>>;
}

export const LessonPlanForm: React.FC<LessonPlanFormProps> = ({
  formData,
  handleInputChange,
  isFormValid,
  isGenerating,
  handleGenerateNow,
  handleEnhancePrompt,
  saranaPrasarana,
  setSaranaPrasarana,
  keteranganSarpras,
  setKeteranganSarpras,
  gayaBelajar,
  setGayaBelajar,
  selectedLearningGoals,
  setSelectedLearningGoals,
}) => {
  const [searchLearningGoal, setSearchLearningGoal] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const generatedLearningGoals = useMemo(
    () => createDynamicLearningGoals(formData.topik),
    [formData.topik]
  );
  const filteredLearningGoals = useMemo(() => {
    let goals = generatedLearningGoals;

    if (searchLearningGoal.trim()) {
      goals = generatedLearningGoals.filter(goal =>
        goal.toLowerCase().includes(searchLearningGoal.toLowerCase())
      );
    }

    // Filter out already selected goals
    return goals.filter(goal => !selectedLearningGoals.includes(goal));
  }, [searchLearningGoal, generatedLearningGoals, selectedLearningGoals]);


  const { user } = useAuth();

  const handleEnhanceClick = async () => {
    handleEnhancePrompt();

    if (!user?.id) {
      console.error("User tidak ditemukan untuk gamifikasi");
      return;
    }

    try {
      updateChallengeProgress(user.id, "enhance_prompt").then(result => {
        if (result.success && result.data?.challenge_completed && result.data.points_awarded > 0) {
          toast.success(`Tantangan Selesai: +${result.data.points_awarded} Poin!`);
        } else if (result.error) {
          console.error("Gagal update challenge 'enhance_prompt':", result.error);
        }
      });

    } catch (error) {
      console.error("Error memicu challenge 'enhance_prompt':", error);
    }
  };

  const getKelasOptions = () => {
    const jenjang = formData.jenjangPendidikan;
    if (jenjang === "tk") return ["TK-A", "TK-B"];
    if (jenjang === "paud") return ["PAUD-A", "PAUD-B"];
    if (jenjang === "sd")
      return ["Kelas 1", "Kelas 2", "Kelas 3", "Kelas 4", "Kelas 5", "Kelas 6"];
    if (jenjang === "smp") return ["Kelas 7", "Kelas 8", "Kelas 9"];
    if (jenjang === "sma" || jenjang === "smk")
      return ["Kelas 10", "Kelas 11", "Kelas 12"];
    return [];
  };

  return (
    <div className="space-y-6">
      {/* Mata Pelajaran */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Mata Pelajaran
        </label>
        <Input
          value={formData.mataPelajaran}
          onChange={e => handleInputChange("mataPelajaran", e.target.value)}
          placeholder="Ketik mata pelajaran"
          className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white"
        />
      </div>

      {/* Jenjang Pendidikan */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Jenjang Pendidikan
        </label>
        <Select
          value={formData.jenjangPendidikan}
          onValueChange={value => handleInputChange("jenjangPendidikan", value)}
        >
          <SelectTrigger className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white">
            <SelectValue placeholder="Pilih Jenjang Pendidikan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paud">
              PAUD (Pendidikan Anak Usia Dini)
            </SelectItem>
            <SelectItem value="tk">TK (Taman Kanak-kanak)</SelectItem>
            <SelectItem value="sd">SD (Sekolah Dasar)</SelectItem>
            <SelectItem value="smp">SMP (Sekolah Menengah Pertama)</SelectItem>
            <SelectItem value="sma">SMA (Sekolah Menengah Atas)</SelectItem>
            <SelectItem value="smk">SMK (Sekolah Menengah Kejuruan)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Kelas (dependent on Jenjang) */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Kelas
        </label>
        <Select
          value={formData.kelas}
          onValueChange={value => handleInputChange("kelas", value)}
          disabled={!formData.jenjangPendidikan}
        >
          <SelectTrigger className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white disabled:opacity-60 disabled:cursor-not-allowed">
            <SelectValue
              placeholder={
                formData.jenjangPendidikan
                  ? "Pilih Kelas"
                  : "Pilih Jenjang terlebih dahulu"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {getKelasOptions().map(opt => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Pilih Kurikulum */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Pilih Kurikulum
        </label>
        <Select
          value={formData.kurikulum}
          onValueChange={value => handleInputChange("kurikulum", value)}
        >
          <SelectTrigger className="h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 bg-white">
            <SelectValue placeholder="Pilih Kurikulum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="kurikulum-merdeka">
              Kurikulum Merdeka 2025/2026
            </SelectItem>
            <SelectItem value="k13">K-13</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Topik */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[#373e46]">
          Topik
        </label>
        <Textarea
          value={formData.topik}
          onChange={e => handleInputChange("topik", e.target.value)}
          placeholder="Kerajaan Hindu di Indonesia"
          className="min-h-[80px] rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white"
        />
      </div>

      {/* Kondisi Sarana & Prasarana */}
      <div className="relative overflow-hidden bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100/40 via-purple-100/30 to-transparent rounded-full blur-3xl -z-0 pointer-events-none"></div>
        <div className="relative">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg shadow-purple-500/20">
                <Monitor className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  Kondisi Sarana dan Prasarana
                  {/* <span className="inline-flex items-center justify-center" title="AI akan menyesuaikan rekomendasi media dengan fasilitas yang tersedia.">
                    <Info className="w-4 h-4 text-gray-400" />
                  </span> */}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Pilih fasilitas yang tersedia di sekolah/kelas Anda
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-5">
            {(
              [
                {
                  key: "proyektor",
                  label: "Proyektor",
                  icon: <Monitor className="w-4 h-4" />,
                  color: "from-purple-500 to-purple-600",
                },
                {
                  key: "internet",
                  label: "Internet",
                  icon: <Wifi className="w-4 h-4" />,
                  color: "from-blue-500 to-blue-600",
                },
                {
                  key: "laboratorium",
                  label: "Laboratorium",
                  icon: <FlaskConical className="w-4 h-4" />,
                  color: "from-indigo-500 to-indigo-600",
                },
                {
                  key: "papanTulisDigital",
                  label: "Papan Tulis Digital",
                  icon: <Presentation className="w-4 h-4" />,
                  color: "from-purple-500 to-pink-500",
                },
                {
                  key: "komputer",
                  label: "Komputer/Laptop",
                  icon: <Laptop className="w-4 h-4" />,
                  color: "from-cyan-500 to-cyan-600",
                },
                {
                  key: "lcdTv",
                  label: "LCD/TV",
                  icon: <Tv className="w-4 h-4" />,
                  color: "from-violet-500 to-violet-600",
                },
                {
                  key: "perpustakaan",
                  label: "Perpustakaan",
                  icon: <BookOpen className="w-4 h-4" />,
                  color: "from-amber-500 to-amber-600",
                },
              ] as {
                key: keyof SaranaPrasarana;
                label: string;
                icon: React.ReactNode;
                color: string;
              }[]
            ).map(item => {
              const active = saranaPrasarana[item.key];
              return (
                <div
                  key={item.key}
                  onClick={() =>
                    setSaranaPrasarana({
                      ...saranaPrasarana,
                      [item.key]: !saranaPrasarana[item.key],
                    })
                  }
                  className={`group relative overflow-hidden flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    active
                      ? "bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300 shadow-sm"
                      : "bg-gray-50/50 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="min-w-0 flex items-center gap-3">
                    <div
                      className={`p-2.5 rounded-lg transition-all duration-200 ${
                        active
                          ? `bg-gradient-to-br ${item.color} shadow-md`
                          : "bg-white border border-gray-200 group-hover:border-gray-300"
                      }`}
                    >
                      <span
                        className={`${
                          active
                            ? "text-white"
                            : "text-gray-400 group-hover:text-gray-500"
                        }`}
                      >
                        {item.icon}
                      </span>
                    </div>
                    <span
                      className={`text-sm font-medium transition-colors truncate ${
                        active ? "text-purple-900" : "text-gray-700"
                      }`}
                    >
                      {item.label}
                    </span>
                  </div>
                  {/* Styled checkbox as switch */}
                  <label
                    className="relative inline-flex items-center cursor-pointer ml-auto shrink-0"
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!saranaPrasarana[item.key]}
                      onChange={e =>
                        setSaranaPrasarana({
                          ...saranaPrasarana,
                          [item.key]: e.target.checked,
                        })
                      }
                    />
                    <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-purple-600 transition-colors"></div>
                    <div
                      className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        saranaPrasarana[item.key] ? "translate-x-4" : ""
                      }`}
                    ></div>
                  </label>
                </div>
              );
            })}
          </div>

          {/* Keterangan Tambahan */}
          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keterangan Tambahan (Opsional)
            </label>
            <textarea
              value={keteranganSarpras}
              onChange={e => setKeteranganSarpras(e.target.value)}
              placeholder="Contoh: Proyektor hanya tersedia di ruang multimedia, WiFi kadang tidak stabil..."
              rows={3}
              className="w-full px-4 py-3 bg-white/80 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all resize-none text-sm hover:border-gray-300"
            />
          </div>
        </div>
      </div>

      {/* Tujuan Pembelajaran */}
      <div className="relative bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-purple-100/40 via-pink-100/30 to-transparent rounded-full blur-3xl -z-0 pointer-events-none"></div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg shadow-purple-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                Tujuan Pembelajaran
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {formData.kurikulum.includes("Kurikulum Merdeka")
                  ? "Pilih tujuan pembelajaran sesuai topik Anda"
                  : "Jelaskan tujuan pembelajaran untuk kelas Anda"}
              </p>
            </div>
          </div>
        </div>

        {formData.kurikulum === "kurikulum-merdeka" ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
              <input
                type="text"
                placeholder="Ketik untuk mencari tujuan pembelajaran..."
                value={searchLearningGoal}
                onChange={e => setSearchLearningGoal(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className="w-full pl-12 pr-4 py-3.5 bg-white/80 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-400 transition-all hover:border-gray-300 placeholder:text-gray-400 text-sm"
              />
              {searchLearningGoal && (
                <button
                  onClick={() => setSearchLearningGoal("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}

              {(searchLearningGoal || isSearchFocused) && (
                <div className="absolute z-50 w-full mt-2 bg-white border-2 border-purple-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                  {filteredLearningGoals.length > 0 ? (
                    <div className="p-1">
                      {filteredLearningGoals.map(goal => (
                        <button
                          key={goal}
                          onMouseDown={e => {
                            e.preventDefault(); // Prevent blur
                            setSelectedLearningGoals(prev => [...prev, goal]);
                            setSearchLearningGoal("");
                            setIsSearchFocused(false);
                            // Remove focus from input to make cursor neutral
                            const inputElement = document.querySelector(
                              'input[placeholder*="tujuan pembelajaran"]'
                            ) as HTMLInputElement;
                            if (inputElement) {
                              inputElement.blur();
                            }
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 rounded-lg transition-all group"
                        >
                          <p className="text-sm text-gray-700 group-hover:text-purple-900 leading-relaxed">
                            {goal}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Search className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">
                        Tidak ada tujuan pembelajaran yang sesuai
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Coba kata kunci yang berbeda
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Display selected learning goals */}
            {selectedLearningGoals.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">
                  Tujuan Pembelajaran Terpilih {selectedLearningGoals.length}{" "}
                  goals:
                </h4>
                <div className="space-y-2">
                  {selectedLearningGoals.map((goal, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg"
                    >
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-1.5 bg-purple-500 rounded-lg flex-shrink-0 mt-0.5">
                          <svg
                            className="w-3.5 h-3.5 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-sm text-purple-900 leading-relaxed font-medium">
                          {goal}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedLearningGoals(prev =>
                            prev.filter((_, i) => i !== index)
                          );
                        }}
                        className="ml-2 p-1.5 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-colors flex-shrink-0"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback for old single learning goal (if no multiple selections) */}
            {formData.tujuanPembelajaran &&
              !searchLearningGoal &&
              selectedLearningGoals.length === 0 && (
                <div className="relative p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-purple-500 rounded-lg flex-shrink-0 mt-0.5">
                      <svg
                        className="w-3.5 h-3.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-purple-900 flex-1 leading-relaxed font-medium">
                      {formData.tujuanPembelajaran}
                    </p>
                    <button
                      onClick={() =>
                        handleInputChange("tujuanPembelajaran", "")
                      }
                      className="p-1.5 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition-colors flex-shrink-0"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
          </div>
        ) : (
          <Textarea
            value={formData.tujuanPembelajaran}
            onChange={e =>
              handleInputChange("tujuanPembelajaran", e.target.value)
            }
            placeholder="Siswa memahami alur perdagangan"
            className="min-h-[80px] rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white"
          />
        )}

        {/* Kriteria Tambahan */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-[#373e46] mt-5">
            Kriteria Tambahan (Wajib)
          </label>
          <Textarea
            value={formData.kriteriaTambahan}
            onChange={e =>
              handleInputChange("kriteriaTambahan", e.target.value)
            }
            placeholder="Siswa belum pernah mendalam alur perdagangan maritim; saya bisa pakai inkuiri terbimbing 2 x 45 menit; media peta interaktif + rangkuman digital."
            className="min-h-[100px] rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors text-sm px-4 py-3 resize-none bg-white"
          />
        </div>
      </div>

      {/* Gaya Belajar Siswa */}
      <div className="relative overflow-hidden bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-100/40 via-blue-100/30 to-transparent rounded-full blur-3xl -z-0 pointer-events-none"></div>
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl shadow-lg shadow-purple-500/20">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  Gaya Belajar Siswa
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Pilih gaya belajar yang dominan di kelas Anda
                </p>
              </div>
            </div>
          </div>

          {/* Grid Gaya Belajar - 3 columns */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {/* Audio */}
            <div
              onClick={() =>
                setGayaBelajar({ ...gayaBelajar, audio: !gayaBelajar.audio })
              }
              className={`group relative overflow-hidden flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                gayaBelajar.audio
                  ? "bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300 shadow-sm"
                  : "bg-gray-50/50 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className="min-w-0 flex items-center gap-3">
                <div
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    gayaBelajar.audio
                      ? "bg-gradient-to-br from-purple-500 to-purple-600 shadow-md shadow-purple-500/30"
                      : "bg-white border border-gray-200 group-hover:border-gray-300"
                  }`}
                >
                  <Headphones
                    className={`${
                      gayaBelajar.audio
                        ? "text-white"
                        : "text-gray-400 group-hover:text-gray-500"
                    } w-4 h-4`}
                  />
                </div>
                <span
                  className={`text-sm font-medium transition-colors truncate ${
                    gayaBelajar.audio ? "text-purple-900" : "text-gray-700"
                  }`}
                >
                  Audio
                </span>
              </div>
              <label
                className="relative inline-flex items-center cursor-pointer ml-auto shrink-0"
                onClick={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={gayaBelajar.audio}
                  onChange={e =>
                    setGayaBelajar({ ...gayaBelajar, audio: e.target.checked })
                  }
                />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-purple-600 transition-colors"></div>
                <div
                  className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    gayaBelajar.audio ? "translate-x-4" : ""
                  }`}
                ></div>
              </label>
            </div>

            {/* Visual */}
            <div
              onClick={() =>
                setGayaBelajar({ ...gayaBelajar, visual: !gayaBelajar.visual })
              }
              className={`group relative overflow-hidden flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                gayaBelajar.visual
                  ? "bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300 shadow-sm"
                  : "bg-gray-50/50 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className="min-w-0 flex items-center gap-3">
                <div
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    gayaBelajar.visual
                      ? "bg-gradient-to-br from-blue-500 to-blue-600 shadow-md shadow-blue-500/30"
                      : "bg-white border border-gray-200 group-hover:border-gray-300"
                  }`}
                >
                  <Eye
                    className={`${
                      gayaBelajar.visual
                        ? "text-white"
                        : "text-gray-400 group-hover:text-gray-500"
                    } w-4 h-4`}
                  />
                </div>
                <span
                  className={`text-sm font-medium transition-colors truncate ${
                    gayaBelajar.visual ? "text-purple-900" : "text-gray-700"
                  }`}
                >
                  Visual
                </span>
              </div>
              <label
                className="relative inline-flex items-center cursor-pointer ml-auto shrink-0"
                onClick={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={gayaBelajar.visual}
                  onChange={e =>
                    setGayaBelajar({ ...gayaBelajar, visual: e.target.checked })
                  }
                />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-purple-600 transition-colors"></div>
                <div
                  className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    gayaBelajar.visual ? "translate-x-4" : ""
                  }`}
                ></div>
              </label>
            </div>

            {/* Kinestetik */}
            <div
              onClick={() =>
                setGayaBelajar({
                  ...gayaBelajar,
                  kinestetik: !gayaBelajar.kinestetik,
                })
              }
              className={`group relative overflow-hidden flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                gayaBelajar.kinestetik
                  ? "bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300 shadow-sm"
                  : "bg-gray-50/50 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className="min-w-0 flex items-center gap-3">
                <div
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    gayaBelajar.kinestetik
                      ? "bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-md shadow-indigo-500/30"
                      : "bg-white border border-gray-200 group-hover:border-gray-300"
                  }`}
                >
                  <Hand
                    className={`${
                      gayaBelajar.kinestetik
                        ? "text-white"
                        : "text-gray-400 group-hover:text-gray-500"
                    } w-4 h-4`}
                  />
                </div>
                <span
                  className={`text-sm font-medium transition-colors break-words ${
                    gayaBelajar.kinestetik ? "text-purple-900" : "text-gray-700"
                  }`}
                >
                  Kinestetik
                </span>
              </div>
              <label
                className="relative inline-flex items-center cursor-pointer ml-auto shrink-0"
                onClick={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={gayaBelajar.kinestetik}
                  onChange={e =>
                    setGayaBelajar({
                      ...gayaBelajar,
                      kinestetik: e.target.checked,
                    })
                  }
                />
                <div className="w-10 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-purple-600 transition-colors"></div>
                <div
                  className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    gayaBelajar.kinestetik ? "translate-x-4" : ""
                  }`}
                ></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Enhance Prompt Button */}
      <div className="flex justify-start">
        <Button
          onClick={handleEnhanceClick}
          disabled={!isFormValid}
          variant="outline"
          className="flex items-center gap-2 text-[#6b2cfe] border-[#6b2cfe] hover:bg-[#6b2cfe] hover:text-white rounded-xl px-4 py-2 transition-all duration-300"
        >
          <SparklesIcon className="w-4 h-4" />
          Enhance Prompt
        </Button>
      </div>

      {/* Generate Button */}
      <div className="flex justify-center pt-6">
        <Button
          onClick={handleGenerateNow}
          disabled={!isFormValid || isGenerating}
          className={`w-full h-12 font-semibold rounded-xl transition-all duration-300 ${
            isFormValid && !isGenerating
              ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white hover:shadow-lg"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center gap-2 w-full">
              <div className="flex items-center gap-3">
                <SparklesIcon className="w-5 h-5 animate-pulse" />
                <span>Generate Hasil</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <SparklesIcon className="w-5 h-5" />
              Generate Now
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};
